import { useState, useEffect, useCallback } from 'react';
import {
  BoxSection,
  ButtonDefault,
  ConfirmModal,
  TextL,
  TextS,
} from '../../../../../../app/components';
import { TRegisterBusinessDataRequest } from '../../../../context';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import { Popover, useTheme } from '@mui/material';
import {
  ButtonArea,
  LegalPersonViewKycTabPopOver,
  TableWrapper,
} from './styles';
import { Heading3, Heading4, Table, TextM } from 'app/components';
import {
  TableBodyCellContainer,
  TableHeadCellContainer,
} from 'app/styles/components/Table';
import { EmptyArea } from 'modules/management/components';
import { useCustomerService } from 'modules/customer/services';
import { formatCNPJ, formatCPF } from 'app/helpers';
import { KycStatus, TBusinessKyc, TKyc } from 'modules/customer/interfaces/kyc';
import { capitalize } from 'app/utils/normalizer';
import { InfoOutlined, RefreshOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';

type ILegalPersonViewKycTabProps = {
  business: TRegisterBusinessDataRequest;
};

const tableHeader = [
  {
    id: '1',
    content: (
      <TableHeadCellContainer>
        <div />
      </TableHeadCellContainer>
    ),
  },
  {
    id: '2',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Criado em</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '3',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Status</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '4',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Situação do CNPJ</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '5',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Quadro Social</TextL>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '6',
    content: (
      <TableHeadCellContainer>
        <TextL weight="bold">Atividade</TextL>
      </TableHeadCellContainer>
    ),
  },
];

const LegalPersonViewKycTab = ({ business }: ILegalPersonViewKycTabProps) => {
  const theme = useTheme();
  const [isConfirmRequestKycOpen, toggleConfirmRequestModalOpen] =
    useState(false);
  const [kycList, updateKycList] = useState<TKyc<TBusinessKyc>[]>();
  const navigate = useNavigate();
  const { isProfileAuthorized } = usePermify();
  const {
    getBusinessKyc,
    requestBusinessKyc,
    getBusinessKycLoading,
    requestBusinessKycLoading,
  } = useCustomerService();

  const reloadData = useCallback(() => {
    return getBusinessKyc(business.taxpayer_id).then((kyc) => {
      if (kyc)
        updateKycList(
          kyc.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          ),
        );
    });
  }, [business]);

  useEffect(() => {
    if (isProfileAuthorized(EAccountPermissions.READ_KYC)) {
      reloadData();
    } else {
      navigate('/');
    }
  }, [business]);

  const onRequestKyc = useCallback(() => {
    requestBusinessKyc(business.legal_name, business.taxpayer_id).then(() => {
      reloadData().then(() => {
        toggleConfirmRequestModalOpen(false);
      });
    });
  }, [business]);

  return (
    <BoxSection level="wrapper">
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Heading3
          style={{ color: theme.palette.brand.secondary.base, width: '100%' }}
        >
          Background Check
          {kycList && kycList.length > 0 && (
            <Heading4
              style={{
                fontSize: '14px',
                paddingTop: '5px',
              }}
            >
              <strong>{`Última consulta: ${new Date(
                kycList[0].created_at,
              ).toLocaleDateString('pt-br')} às ${new Date(
                kycList[0].created_at,
              ).toLocaleTimeString('pt-br')}`}</strong>
            </Heading4>
          )}
        </Heading3>
        {kycList &&
          kycList.length > 0 &&
          isProfileAuthorized(EAccountPermissions.CREATE_KYC) && (
            <ButtonArea>
              <ButtonDefault
                style={{ maxWidth: '250px' }}
                onClick={() => toggleConfirmRequestModalOpen(true)}
              >
                <RefreshOutlined
                  style={{
                    color: theme.palette.brand.primary.base as string,
                    marginRight: '5px',
                    fontSize: '22px',
                  }}
                />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.brand.primary.base }}
                >
                  Atualizar consulta
                </TextM>
              </ButtonDefault>
            </ButtonArea>
          )}
      </div>
      {kycList && kycList.length > 0 ? (
        <TableWrapper>
          <Table
            tableHeadCell={tableHeader}
            tableBodyContent={kycList.map((kycItem, index) => {
              const { result } = kycItem;
              return {
                id: `${result?.pj_fantasy_name}-${String(index)}`,
                cells: [
                  {
                    id: '1',
                    content: (
                      <TableBodyCellContainer>
                        <PopupState
                          variant="popover"
                          popupId={`${result?.pj_fantasy_name}-details-${index}-popup`}
                        >
                          {(popupState) => (
                            <>
                              <ButtonDefault
                                {...bindTrigger(popupState)}
                                style={{
                                  border: 'none',
                                  fontWeight: 'normal',
                                  background: 'transparent',
                                  textDecoration: 'underline',
                                  justifyContent: 'flex-start',
                                  padding: '0px',
                                }}
                              >
                                <InfoOutlined />
                              </ButtonDefault>
                              <Popover
                                {...bindPopover(popupState)}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'left',
                                }}
                              >
                                <LegalPersonViewKycTabPopOver>
                                  <TextS>
                                    {result?.pj_ofac_sanctions_result ??
                                      KycStatus[kycItem.status]}
                                  </TextS>
                                </LegalPersonViewKycTabPopOver>
                              </Popover>
                            </>
                          )}
                        </PopupState>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '2',
                    content: (
                      <TableBodyCellContainer>
                        <TextS>
                          {`${new Date(kycItem.created_at).toLocaleDateString(
                            'pt-br',
                          )} às ${new Date(
                            kycItem.created_at,
                          ).toLocaleTimeString('pt-br')}`}
                        </TextS>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '3',
                    content: (
                      <TableBodyCellContainer>
                        <TextS>{KycStatus[kycItem.status]}</TextS>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '4',
                    content: (
                      <TableBodyCellContainer>
                        <TextS>
                          {result?.pj_status
                            ? capitalize(result.pj_status)
                            : '-'}
                        </TextS>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '5',
                    content: (
                      <TableBodyCellContainer>
                        {result?.pj_qsa ? (
                          <PopupState
                            variant="popover"
                            popupId={`${result?.pj_fantasy_name}-qsa-${index}-popup`}
                          >
                            {(popupState) => (
                              <>
                                <ButtonDefault
                                  {...bindTrigger(popupState)}
                                  style={{
                                    border: 'none',
                                    fontWeight: 'normal',
                                    background: 'transparent',
                                    textDecoration: 'underline',
                                    justifyContent: 'flex-start',
                                    padding: '0px',
                                  }}
                                >
                                  Visualizar
                                </ButtonDefault>
                                <Popover
                                  {...bindPopover(popupState)}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                  }}
                                >
                                  {result?.pj_qsa.map((qsaItem) => {
                                    return (
                                      <LegalPersonViewKycTabPopOver
                                        key={qsaItem.cpf}
                                      >
                                        <TextS>
                                          <strong>Nome:</strong> {qsaItem.name}
                                        </TextS>
                                        <TextS>
                                          <strong>CPF:</strong>{' '}
                                          {formatCPF(qsaItem.cpf)}
                                        </TextS>
                                        <TextS>
                                          <strong>Função:</strong>{' '}
                                          {qsaItem.function}
                                        </TextS>
                                        <TextS>
                                          <strong>Representante Legal:</strong>{' '}
                                          {qsaItem.isLegalRepresentative
                                            ? 'Sim'
                                            : 'Não'}
                                        </TextS>
                                      </LegalPersonViewKycTabPopOver>
                                    );
                                  })}
                                </Popover>
                              </>
                            )}
                          </PopupState>
                        ) : (
                          '-'
                        )}
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '6',
                    content: (
                      <TableBodyCellContainer>
                        {result?.pj_main_activity ?? '-'}
                      </TableBodyCellContainer>
                    ),
                  },
                ],
              };
            })}
          />
        </TableWrapper>
      ) : (
        <EmptyArea
          message="Nenhuma consulta de Background Check realizada"
          createButtonText="Realizar consulta"
          onCreateClick={
            isProfileAuthorized(EAccountPermissions.CREATE_KYC)
              ? () => toggleConfirmRequestModalOpen(true)
              : undefined
          }
          isLoading={getBusinessKycLoading}
        >
          {null}
        </EmptyArea>
      )}
      <ConfirmModal
        isOpen={isConfirmRequestKycOpen}
        title="A realização desta consulta gera custo para o Originador."
        color="primary"
        btnConfirmText="Confirmar"
        isLoading={requestBusinessKycLoading}
        handleConfirm={onRequestKyc}
        handleClose={() => toggleConfirmRequestModalOpen(false)}
        handleCancel={() => toggleConfirmRequestModalOpen(false)}
      >
        Consultas de Background Check precisam do consentimento Livre, Informado
        e Inambíguo da Empresa. Você confirma que obteve consentimento do
        titular do CNPJ: <strong>{formatCNPJ(business.taxpayer_id)}</strong>?
      </ConfirmModal>
    </BoxSection>
  );
};

export default LegalPersonViewKycTab;
